import { 
  ActivityDataPoint, 
  SentimentCount, 
  CategoryCount, 
  PeriodType, 
  CustomDateRange,
  BrandDataPoint
} from '../types'; // Adjusted path
import { groupClippingsByPeriod } from './dataProcessing'; // Already in utils
import { getSentimentColorValue, getCategoryColor } from './styleUtils'; // Already in utils

// Add utility function outside the component for data processing
export const processClippingsData = ( // Added export
  clippings: any[], 
  selectedPeriod: PeriodType, 
  customDateRange: CustomDateRange,
  selectedBrand?: string,
  showAllCategories = false,
  dashboardFilters = false,
  activeDashboardFilters: { [key: string]: string[] } = {}
) => {
  let filteredClippings = clippings;

  // Apply filters based on dashboardFilters prop
  if (dashboardFilters && Object.keys(activeDashboardFilters).length > 0) {
    // Apply all filters from the dashboard
    filteredClippings = clippings.filter((clip: any) => {
      // Check if the clip matches ALL active filters
      return Object.entries(activeDashboardFilters).every(([filterKey, filterValues]) => {
        if (!filterValues || filterValues.length === 0) return true; // No values for this filter, skip

        let clipValue: string | null = null;

        // Map filter keys to potential clip properties
        switch (filterKey) {
          case 'brand':
          case 'brandName': // Handle potential variations
            clipValue = clip.brandName || clip.brand_name || clip.brand || null;
            break;
          case 'category':
            clipValue = clip.category || null;
            break;
          case 'source':
          case 'sourceName': // Handle potential variations
            clipValue = clip.source || clip.sourceName || clip.source_name || null;
            break;
          case 'sentiment':
             clipValue = clip.sentiment || null; // Assuming sentiment is stored directly
             break;
          case 'startDate':
          case 'endDate':
             // These are handled separately for date filtering, not clip properties
             return true; // Don't filter individual clips based on these date filters
          // Add cases for other potential filter keys if necessary
          default:
            console.warn(`Unhandled dashboard filter key: ${filterKey}`);
            return true; // Don't filter out if key is unknown
        }

        // Check if the clip's value (case-insensitive trim) is included in the filter's allowed values
        return clipValue !== null && filterValues.some(filterVal => 
           (clipValue as string).trim().toLowerCase() === filterVal.trim().toLowerCase()
        );
      });
    });
  } else if (!dashboardFilters && selectedBrand && selectedBrand !== 'All Brands') {
    // Original behavior: Apply only the selectedBrand filter if not using dashboard filters
    filteredClippings = clippings.filter((clip: any) => {
      const clipBrand = clip.brandName || clip.brand_name || clip.brand || '';
      return clipBrand.trim() === selectedBrand.trim();
    });
  }
  // --- End of updated filtering logic ---
  
  // Group by period and get sentiment breakdown
  const activityData = groupClippingsByPeriod(filteredClippings, selectedPeriod, customDateRange);
  
  // Calculate max value for graph scaling
  const max = Math.max(...activityData.map(item => item.value), 1);
  const maxValue = max > 0 ? max : 10;
  
  // --- Recalculate Breakdowns Per Period --- 
  const categoriesByPeriod: { [period: string]: CategoryCount[] } = {};
  const brandsByPeriod: { [period: string]: CategoryCount[] } = {};
  const sourcesByPeriod: { [period: string]: CategoryCount[] } = {};
  
  // Initialize maps for each period found in activityData
  activityData.forEach(periodData => {
    categoriesByPeriod[periodData.label] = [];
    brandsByPeriod[periodData.label] = [];
    sourcesByPeriod[periodData.label] = [];
  });

  // Temporary maps to aggregate counts within each period
  const tempCategoryCounts: { [periodLabel: string]: { [category: string]: number } } = {};
  const tempBrandCounts: { [periodLabel: string]: { [brand: string]: number } } = {};
  const tempSourceCounts: { [periodLabel: string]: { [source: string]: number } } = {};

  // Track unique overall items
  const seenCategories = new Set<string>();
  const seenBrands = new Set<string>();
  const seenSources = new Set<string>();

  // Process all filtered clippings again to assign categories/brands/sources to the correct period
  filteredClippings.forEach((clip: any) => {
    // Determine the correct periodLabel for this clip using the *same logic* as groupClippingsByPeriod
    const clipDateValue = clip.date || clip.publishDate || clip.publish_date || clip.created_at || null;
    if (!clipDateValue) return;
    const clipDate = new Date(clipDateValue);
    if (isNaN(clipDate.getTime())) return;
    
    let periodLabel: string | null = null;
    // --- Logic to determine periodLabel based on selectedPeriod --- 
    // (This must precisely match the label generation in groupClippingsByPeriod)
    if (selectedPeriod === 'Monthly') {
      const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      periodLabel = `${months[clipDate.getMonth()]} ${clipDate.getFullYear()}`;
    } else if (selectedPeriod === 'Quarterly') {
      const quarter = Math.floor(clipDate.getMonth() / 3) + 1;
      periodLabel = `Q${quarter} ${clipDate.getFullYear()}`;
    } else if (selectedPeriod === 'Weekly') {
        let tempDate = new Date(clipDate);
        tempDate.setDate(tempDate.getDate() - tempDate.getDay()); // Go to start of week (Sunday)
        // Calculate the end date (Saturday) of the week the clipDate falls into
        // This *must* match the label generation in groupClippingsByPeriod (dataProcessing.ts)
        let endOfWeekDate = new Date(tempDate);
        endOfWeekDate.setDate(tempDate.getDate() + 6); // Sunday + 6 days = Saturday
        // The label is the end date string in MM/DD/YY format
        periodLabel = endOfWeekDate.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: '2-digit' });
    } else { // Default to Daily
      periodLabel = clipDate.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit' });
    }
    // --- End of periodLabel logic ---

    // Ensure the calculated periodLabel exists in our breakdown maps
    if (periodLabel && categoriesByPeriod.hasOwnProperty(periodLabel)) {
        // Initialize temp counts for this period if needed
        if (!tempCategoryCounts[periodLabel]) tempCategoryCounts[periodLabel] = {};
        if (!tempBrandCounts[periodLabel]) tempBrandCounts[periodLabel] = {};
        if (!tempSourceCounts[periodLabel]) tempSourceCounts[periodLabel] = {};

        // Process categories
        const category = clip.category || 'Uncategorized'; // Use a default if empty
        tempCategoryCounts[periodLabel][category] = (tempCategoryCounts[periodLabel][category] || 0) + 1;
        seenCategories.add(category);
        
        // Process brands
        const brand = clip.brandName || clip.brand_name || clip.brand || 'Unknown Brand';
        tempBrandCounts[periodLabel][brand] = (tempBrandCounts[periodLabel][brand] || 0) + 1;
        seenBrands.add(brand);
        
        // Process sources
        const source = clip.source || clip.sourceName || clip.source_name || 'Unknown Source';
        tempSourceCounts[periodLabel][source] = (tempSourceCounts[periodLabel][source] || 0) + 1;
        seenSources.add(source);
    }
  });

  // Convert temporary counts into the final structure (CategoryCount[]) for each period
  Object.keys(categoriesByPeriod).forEach(period => {
    if (tempCategoryCounts[period]) {
      categoriesByPeriod[period] = Object.entries(tempCategoryCounts[period])
        .map(([category, count], index) => ({ category, count, color: getCategoryColor(category, index) }))
        .sort((a, b) => b.count - a.count);
    }
    if (tempBrandCounts[period]) {
      brandsByPeriod[period] = Object.entries(tempBrandCounts[period])
        .map(([brand, count], index) => ({ category: brand, count, color: getCategoryColor(brand, index) })) // Note: using category field for brand name
        .sort((a, b) => b.count - a.count);
    }
    if (tempSourceCounts[period]) {
      sourcesByPeriod[period] = Object.entries(tempSourceCounts[period])
        .map(([source, count], index) => ({ category: source, count, color: getCategoryColor(source, index) })) // Note: using category field for source name
        .sort((a, b) => b.count - a.count);
    }
  });

  // --- Prepare Overall Data (Aggregated across all periods) ---
  
  // Overall Sentiment Data (reuse calculation from groupClippingsByPeriod for consistency if possible, or recalculate)
  const overallSentimentCounts: { [key: string]: number } = { 'Very Positive': 0, 'Positive': 0, 'Neutral': 0, 'Negative': 0, 'Very Negative': 0 };
  activityData.forEach(period => {
    Object.entries(period.sentiments).forEach(([sentiment, count]) => {
      if (overallSentimentCounts.hasOwnProperty(sentiment)) {
          overallSentimentCounts[sentiment] += count ?? 0;
      }
    });
  });
  const sentimentData: SentimentCount[] = Object.entries(overallSentimentCounts).map(([sentiment, count]) => ({
    sentiment,
    count,
    color: getSentimentColorValue(sentiment)
  }));

  // Overall Category Data
  const overallCategoryCounts: { [category: string]: number } = {};
  Object.values(tempCategoryCounts).forEach(periodCounts => {
      Object.entries(periodCounts).forEach(([category, count]) => {
          overallCategoryCounts[category] = (overallCategoryCounts[category] || 0) + count;
      });
  });
  const categoryData: CategoryCount[] = Array.from(seenCategories).map((category, index) => ({
    category,
    count: overallCategoryCounts[category] || 0,
    color: getCategoryColor(category, index)
  })).sort((a, b) => b.count - a.count);

  // Overall Brand Data (used for pie chart, needs CategoryCount structure)
  const overallBrandCounts: { [brand: string]: number } = {};
   Object.values(tempBrandCounts).forEach(periodCounts => {
      Object.entries(periodCounts).forEach(([brand, count]) => {
          overallBrandCounts[brand] = (overallBrandCounts[brand] || 0) + count;
      });
  });
  const brandCategoriesData: CategoryCount[] = Array.from(seenBrands).map((brand, index) => ({
    category: brand, // The pie chart component expects a 'category' field
    count: overallBrandCounts[brand] || 0,
    color: getCategoryColor(brand, index)
  })).sort((a, b) => b.count - a.count);

  // Overall Source Data
  const overallSourceCounts: { [source: string]: number } = {};
  Object.values(tempSourceCounts).forEach(periodCounts => {
      Object.entries(periodCounts).forEach(([source, count]) => {
          overallSourceCounts[source] = (overallSourceCounts[source] || 0) + count;
      });
  });
  const sourceData: CategoryCount[] = Array.from(seenSources).map((source, index) => ({
    category: source, // The pie chart component expects a 'category' field
    count: overallSourceCounts[source] || 0,
    color: getCategoryColor(source, index)
  })).sort((a, b) => b.count - a.count);

  // Note: brandData (BrandDataPoint[]) is less useful now, we mainly use brandCategoriesData
  const brandData: BrandDataPoint[] = [
    {
      period: 'All',
      brands: overallBrandCounts
    }
  ];

  return {
    activityData: activityData, // Use the already correctly calculated activityData
    maxValue,
    sentimentData,
    categoryData,
    brandData, // Keep for potential other uses, but pie uses brandCategoriesData
    brandCategoriesData, // Use this for the Pie Chart
    sourceData, // Use this for the Pie Chart (assuming it expects CategoryCount)
    categoriesByPeriod,
    brandsByPeriod,
    sourcesByPeriod,
    totalCount: filteredClippings.length
  };
}; 